/**
 * ColorSelect — a form field for choosing a color from the badge utility palette.
 *
 * Renders as a trigger button (showing the selected color dot or placeholder)
 * that opens a popover with a grid of color swatches. Uses react-aria's
 * DialogTrigger + Popover for automatic flip placement.
 */

import { ChevronDown } from '@untitledui/icons';
import { useCallback, useRef } from 'react';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
} from 'react-aria-components';

import { Popover } from '@/lib/base/overlays/popover';
import { cx } from '@/utils/cx';

export interface StageColor {
  value: string;
  label: string;
  className: string;
}

// eslint-disable-next-line react-refresh/only-export-components -- constant co-located with component
export const STAGE_COLORS: StageColor[] = [
  { value: '#717680', label: 'Fog', className: 'bg-utility-gray-500' },
  { value: '#3528B8', label: 'Ocean', className: 'bg-utility-brand-500' },
  { value: '#F04438', label: 'Red', className: 'bg-utility-error-500' },
  { value: '#F79009', label: 'Orange', className: 'bg-utility-warning-500' },
  { value: '#16B364', label: 'Green', className: 'bg-utility-success-500' },
  { value: '#4E5BA6', label: 'Slate', className: 'bg-utility-gray-blue-500' },
  { value: '#0BA5EC', label: 'Sky', className: 'bg-utility-blue-light-500' },
  { value: '#2E90FA', label: 'Blue', className: 'bg-utility-blue-500' },
  { value: '#6172F3', label: 'Twilight', className: 'bg-utility-indigo-500' },
  { value: '#7A5AF8', label: 'Purple', className: 'bg-utility-purple-500' },
  { value: '#EE46BC', label: 'Pink', className: 'bg-utility-pink-500' },
  { value: '#EF6820', label: 'Amber', className: 'bg-utility-orange-500' },
];

export interface ColorSelectProps {
  /** Label shown above the field */
  label?: string;
  /** Currently selected color hex value */
  value: string | null;
  /** Callback when a color is selected or cleared */
  onChange: (colorValue: string | null) => void;
  /** Placeholder text when no color is selected */
  placeholder?: string;
  /** Whether the field is disabled */
  isDisabled?: boolean;
  /** Compact mode — small trigger showing only the swatch dot */
  compact?: boolean;
  /** Accessible label for the trigger */
  'aria-label'?: string;
}

interface ColorSwatchButtonProps {
  color: StageColor;
  isSelected: boolean;
  onSelect: (value: string) => void;
}

function ColorSwatchButton({
  color,
  isSelected,
  onSelect,
}: ColorSwatchButtonProps) {
  const handleClick = useCallback(
    () => onSelect(color.value),
    [onSelect, color.value]
  );
  return (
    <button
      type='button'
      role='radio'
      aria-checked={isSelected}
      aria-label={color.label}
      onClick={handleClick}
      className={cx(
        'flex items-center gap-2 px-2 py-1.5 rounded-md w-full',
        'transition-colors cursor-pointer text-sm',
        isSelected
          ? 'bg-brand-50 ring-2 ring-brand-600'
          : 'hover:bg-bg-secondary',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500'
      )}
    >
      <span
        className='block size-3.5 rounded-full flex-shrink-0'
        style={{ backgroundColor: color.value }}
      />
      <span className='text-text-primary'>{color.label}</span>
    </button>
  );
}

export function ColorSelect({
  label,
  value,
  onChange,
  placeholder = 'Select a color',
  isDisabled = false,
  compact = false,
  'aria-label': ariaLabel,
}: ColorSelectProps) {
  // Store the close function from AriaDialog's render prop in a ref
  // so our callbacks can be stable useCallbacks instead of inline arrows.
  const closeRef = useRef<(() => void) | null>(null);

  const handleSelect = useCallback(
    (colorValue: string) => {
      onChange(colorValue);
      closeRef.current?.();
    },
    [onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(null);
      closeRef.current?.();
    },
    [onChange]
  );

  return (
    <div>
      {label && (
        <label className='block text-sm font-medium text-text-primary mb-2'>
          {label}
        </label>
      )}

      <AriaDialogTrigger>
        {/* Trigger */}
        <AriaButton
          isDisabled={isDisabled}
          aria-label={ariaLabel ?? label ?? 'Select color'}
          className={cx(
            'relative flex items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary ring-inset',
            'text-sm transition duration-100 ease-linear',
            compact ? 'py-2.5 px-2.5 w-fit h-[40px]' : 'py-2 px-3 w-full',
            'data-[pressed]:ring-2 data-[pressed]:ring-brand',
            isDisabled
              ? 'cursor-not-allowed bg-disabled_subtle text-disabled'
              : 'cursor-pointer hover:bg-secondary'
          )}
        >
          {value ? (
            // Render any hex value the field holds — not just palette options —
            // so template/custom colors show their actual swatch.
            <span
              className='block size-3.5 rounded-full flex-shrink-0'
              style={{ backgroundColor: value }}
            />
          ) : (
            <span
              className={cx(
                'text-text-tertiary',
                !compact && 'flex-1 text-left'
              )}
            >
              {compact ? '–' : placeholder}
            </span>
          )}
          <ChevronDown className='w-4 h-4 text-tertiary flex-shrink-0 ml-1.5' />
        </AriaButton>

        {/* Popover — react-aria handles flip placement automatically */}
        <Popover
          size='md'
          placement='bottom end'
          className='min-w-56! max-h-none! w-auto! p-3'
        >
          <AriaDialog className='outline-hidden'>
            {({ close }) => {
              closeRef.current = close;
              return (
                <>
                  {/* Clear option */}
                  <button
                    type='button'
                    onClick={handleClear}
                    className='w-full text-left text-sm text-text-tertiary hover:text-text-primary px-2 py-1.5 rounded-md hover:bg-bg-secondary transition-colors mb-2'
                  >
                    None
                  </button>

                  <div
                    role='radiogroup'
                    aria-label='Color options'
                    className='grid grid-cols-2 gap-0.5'
                  >
                    {STAGE_COLORS.map(color => (
                      <ColorSwatchButton
                        key={color.value}
                        color={color}
                        isSelected={
                          value?.toLowerCase() === color.value.toLowerCase()
                        }
                        onSelect={handleSelect}
                      />
                    ))}
                  </div>
                </>
              );
            }}
          </AriaDialog>
        </Popover>
      </AriaDialogTrigger>
    </div>
  );
}
